import { EventEmitter } from './EventEmitter';
import { IPlatformPrivateEventBase } from './events';

export enum PlatformPrivateEvent {
  WorkerSpawn = 'WorkerSpawn',
  HostEvent = 'HostEvent',
  ApplicationsSpecsReceived = 'ApplicationsSpecsReceived',
  InitApplicationApi = 'InitApplicationApi',
  RunApplication = 'RunApplication',
  getApplicationApi = 'getApplicationApi',
  IframeInit = 'IframeInit',
}

export enum PlatformLifecycleEvent {
  EditorReady = 'EditorReady',
}

export type IPlatformWorkerSpawnEvent = IPlatformPrivateEventBase<
  PlatformPrivateEvent.WorkerSpawn,
  { url: string },
  {
    bi: {
      workerId: string;
      strategy: string;
    };
  }
>;

export type IPlatformEditorReadyLifecycleEvent = IPlatformPrivateEventBase<
  PlatformLifecycleEvent.EditorReady,
  {},
  {}
>;

export type IPlatformHostEvent = IPlatformPrivateEventBase<
  PlatformPrivateEvent.HostEvent,
  {
    type: string;
  },
  { appDefinitionId: string; intent: string }
>;

export type IPlatformApplicationsSpecsReceived = IPlatformPrivateEventBase<
  PlatformPrivateEvent.ApplicationsSpecsReceived,
  {
    appDefinitionIds: string[];
  },
  {}
>;

export type IPlatformRunApplication = IPlatformPrivateEventBase<
  PlatformPrivateEvent.RunApplication,
  {
    appDefinitionId: string;
  },
  {
    bi: {
      appType?: string;
      appDefId?: string;
    };
  }
>;

export type IPlatformGetApplicationApi = IPlatformPrivateEventBase<
  PlatformPrivateEvent.getApplicationApi,
  {},
  {
    bi: {
      targetAppDefId: string;
      scope: 'private' | 'public';
      editorType?: string;
    };
  }
>;

export type IPlatformInitApplicationApi = IPlatformPrivateEventBase<
  PlatformPrivateEvent.InitApplicationApi,
  {
    appDefinitionId: string;
  },
  {
    bi: {
      appDefId: string;
    };
  }
>;

export type IPlatformIframeInit = IPlatformPrivateEventBase<
  PlatformPrivateEvent.IframeInit,
  {},
  {
    bi: {
      frameUrl: string;
      iframeId: string;
    };
  }
>;

export type IPlatformPrivateEvent =
  | IPlatformWorkerSpawnEvent
  | IPlatformEditorReadyLifecycleEvent
  | IPlatformHostEvent
  | IPlatformApplicationsSpecsReceived
  | IPlatformRunApplication
  | IPlatformGetApplicationApi
  | IPlatformInitApplicationApi
  | IPlatformIframeInit;

class PlatformPrivateEventFactories {
  public createWorkerSpawnEvent({
    url,
    workerId,
    strategy,
  }: {
    url: string;
    workerId: string;
    strategy: string;
  }): IPlatformWorkerSpawnEvent {
    return {
      type: PlatformPrivateEvent.WorkerSpawn,
      payload: {
        url,
      },
      meta: {
        bi: {
          evid: PlatformPrivateEventEmitter.biEvents.PLATFORM_WORKER_SPAWN,
          workerId,
          strategy,
        },
      },
    };
  }

  public createRunApplicationEvent(app: {
    appDefinitionId: string;
    type: string;
  }): IPlatformRunApplication {
    return {
      type: PlatformPrivateEvent.RunApplication,
      payload: {
        appDefinitionId: app.appDefinitionId,
      },
      meta: {
        bi: {
          appType: app.type,
          appDefId: app.appDefinitionId,
          evid: PlatformPrivateEventEmitter.biEvents
            .PLATFORM_APP_RUNNER_RUN_APP,
        },
      },
    };
  }

  public createGetApplicationApiEvent(
    targetAppDefId: string,
    scope: 'private' | 'public',
  ): IPlatformGetApplicationApi {
    return {
      type: PlatformPrivateEvent.getApplicationApi,
      payload: {},
      meta: {
        bi: {
          targetAppDefId,
          scope,
          evid: PlatformPrivateEventEmitter.biEvents.PLATFORM_APP_API_GET,
        },
      },
    };
  }

  public createInitApplicationApiEvent(
    appDefinitionId: string,
  ): IPlatformInitApplicationApi {
    return {
      type: PlatformPrivateEvent.InitApplicationApi,
      payload: {
        appDefinitionId,
      },
      meta: {
        bi: {
          appDefId: appDefinitionId,
          evid: PlatformPrivateEventEmitter.biEvents.PLATFORM_APP_API_INIT,
        },
      },
    };
  }

  public createIframeInitEvent(
    frameUrl: string,
    iframeId: string,
  ): IPlatformIframeInit {
    return {
      type: PlatformPrivateEvent.IframeInit,
      payload: {},
      meta: {
        bi: {
          frameUrl,
          iframeId,
          evid: PlatformPrivateEventEmitter.biEvents.PLATFORM_IFRAME_INIT,
        },
      },
    };
  }
}

export class PlatformPrivateEventEmitter extends EventEmitter<IPlatformPrivateEvent> {
  public factories = new PlatformPrivateEventFactories();

  notify(event: IPlatformPrivateEvent) {
    super.notify(event);
  }
}
